import type React from 'react';

import { usePetitionDetailsPageData, usePetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';
import {
	usePageScrollPosition,
	usePromoteCtaDisplayed,
	useUnauthenticatedSignForm,
} from '#src/app/pages/petitionDetails/shared/hooks';
import type { PageScrollPosition } from '#src/app/pages/petitionDetails/shared/hooks';

import { PetitionStatusHeader } from '../../components/PetitionStatusHeader';
import { SignatureStatsClosedContainer, SignatureStatsVictoryContainer } from '../stats/signatureStats';

import { MobileComboButtonContainer } from './MobileComboButton';
import { MobileRespondButtonContainer } from './MobileRespondButton';
import { MobileShareButtonContainer } from './MobileShareButton';
import { MobileSignButtonContainer } from './MobileSignButton';

export type ActionPanelVariant = 'v1' | 'v2';
/**
 * All three docked layouts show the sticky bar from first paint, and none of them ever put two sign
 * CTAs on screen at once. They differ in what happens to the in-flow block:
 *
 * - `sticky-sign` (video-first hero) drops it, and the bar carries the sign CTA alone.
 * - `sticky-from-load` (CHANGE-97346 variant_1) drops it, and the bar carries sign + share.
 * - `sticky-sign-share-inline` (CHANGE-97346 variant_2) keeps it for the share CTA only, and the bar
 *   carries the sign CTA alone.
 */
export type ActionPanelLayout = 'inline-then-sticky' | 'sticky-sign' | 'sticky-from-load' | 'sticky-sign-share-inline';

type Props = {
	layout?: ActionPanelLayout;
	sticky: boolean;
	variant: ActionPanelVariant;
};

const calculateVisibility = (
	layout: ActionPanelLayout,
	sticky: boolean,
	{ beforeSuggestedPetitions, afterDescription, afterMobileActionButton }: PageScrollPosition,
): boolean => {
	if (!sticky) {
		// `sticky-sign-share-inline` keeps the in-flow block, but only for the share CTA — its sign
		// button is suppressed via `hideSignButton` below, so there is still just one sign CTA on
		// screen. The other two docked layouts replace the in-flow block outright.
		return layout === 'inline-then-sticky' || layout === 'sticky-sign-share-inline';
	}

	/**
	 * The standard sticky button appears after the non-sticky button. Layouts that dock from page
	 * load start visible instead.
	 * !afterDescription condition allows sticky button to show in petitions with short descriptions
	 */
	const hasReachedStickyStart = layout !== 'inline-then-sticky' || !!afterMobileActionButton;

	return hasReachedStickyStart && (beforeSuggestedPetitions || !afterDescription);
};

/**
 * Whether this render site paints the share affordance at all — distinct from `ownsShareExposure`,
 * which decides where its `$exposure` fires. Under `inline-then-sticky` both sites render it and
 * only one counts, so the two are not inverses of each other.
 *
 * `sticky-sign-share-inline` splits the two CTAs across the sites: the docked bar takes the sign
 * button, the in-flow block keeps the share affordance, so the affordance is dropped on the sticky
 * side only — the opposite of `sticky-sign`, which drops it everywhere.
 */
const rendersShareAffordance = (layout: ActionPanelLayout, sticky: boolean): boolean => {
	if (layout === 'sticky-sign') return false;

	return !(layout === 'sticky-sign-share-inline' && sticky);
};

/**
 * Which render site the share affordance belongs to, per layout. Drives share-cta-copy `$exposure`,
 * which has to fire from the instance the viewer can actually see and click. The old `!sticky`
 * heuristic only held for `inline-then-sticky`: under `sticky-from-load` it fired from neither site,
 * while the docked bar rendered the treated affordance and took the clicks.
 */
const ownsShareExposure = (layout: ActionPanelLayout, sticky: boolean): boolean => {
	switch (layout) {
		// Not painted at either site (see `rendersShareAffordance`), so neither owns it.
		case 'sticky-sign':
			return false;
		// The in-flow block is gone; the docked bar carries it.
		case 'sticky-from-load':
			return sticky;
		// The docked bar is sign-only here, so the affordance stays with the in-flow block.
		case 'sticky-sign-share-inline':
		case 'inline-then-sticky':
			return !sticky;
		default: {
			// Exhaustiveness guard: a new ActionPanelLayout makes `layout` non-never here, forcing a
			// compile error until it declares which render site owns the affordance.
			const exhaustive: never = layout;
			return exhaustive;
		}
	}
};

export const MobileActionButtonContainer: React.FC<Props> = ({ layout = 'inline-then-sticky', sticky, variant }) => {
	const usePageScrollPositionContext = usePageScrollPosition();
	const {
		data: {
			petition: { status },
		},
	} = usePetitionDetailsPageData();
	const {
		data: { decisionMaker, signed },
	} = usePetitionDetailsPageUserData();
	const promoteCtaDisplayed = usePromoteCtaDisplayed();
	const {
		data: { enabled: unauthenticatedSignFormEnabled },
	} = useUnauthenticatedSignForm();

	const visible = calculateVisibility(layout, sticky, usePageScrollPositionContext);

	if (decisionMaker) {
		return <MobileRespondButtonContainer visible={visible} sticky={sticky} />;
	}

	if (status === 'CLOSED') {
		return (
			<div className="mb-6 flex flex-col gap-5">
				{!sticky && (
					<>
						<PetitionStatusHeader />
						<div className="text-center">
							<SignatureStatsClosedContainer />
						</div>
					</>
				)}
				<MobileShareButtonContainer visible={visible} sticky={sticky} hideSocialProof />
			</div>
		);
	}

	if (status === 'VICTORY') {
		return (
			<div className="mb-6 flex flex-col gap-5">
				{!sticky && (
					<>
						<PetitionStatusHeader />
						<SignatureStatsVictoryContainer textAlign="center" />
					</>
				)}
				<MobileShareButtonContainer visible={visible} sticky={sticky} hideSocialProof />
			</div>
		);
	}

	if (signed && !unauthenticatedSignFormEnabled) {
		return promoteCtaDisplayed ? (
			<MobileComboButtonContainer visible={visible} sticky={sticky} />
		) : (
			<MobileShareButtonContainer visible={visible} sticky={sticky} />
		);
	}

	return (
		<MobileSignButtonContainer
			rendersShareAffordance={rendersShareAffordance(layout, sticky)}
			hideSignButton={layout === 'sticky-sign-share-inline' && !sticky}
			ownsShareExposure={ownsShareExposure(layout, sticky)}
			visible={visible}
			sticky={sticky}
			variant={variant}
		/>
	);
};
